import React, { useCallback, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import { CurriculaSelection } from '../../tree/CurriculaSelection'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { addCurricula } from 'shared/api/mutations'
import { useNotify } from 'shared/ui/Notify'
import RequestDialog from '../../dialogs/RequestDialog'

interface DistrictSchoolItem {
  id: number
  name?: string
  admin_count: number
  teacher_count: number
  student_count: number
}

interface BulkAssignCurriculumProps {
  districtSchools: DistrictSchoolItem[]
  onComplete: () => void
}

const steps = ['Select Curricula', 'Select Schools', 'Confirm & Assign']

export function BulkAssignCurriculum({
  districtSchools,
  onComplete,
}: BulkAssignCurriculumProps) {
  const [activeStep, setActiveStep] = useState(0)
  const [curriculaMap, setCurriculaMap] = useState(
    new Map<string, { productId: number; title: string }>(),
  )
  const [selectedSchoolIndexes, setSelectedSchoolIndexes] = useState<number[]>(
    [],
  )
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0 })

  const notify = useNotify()

  // --- Curriculum selection ---
  // Note: curriculaMap is small (typically <20 items) — Map copy is negligible
  const toggleCurriculum = useCallback(
    (selection: {
      treeId: string
      productId: number
      treeVersion: string
      masterTitle: string
    }) => {
      const key = `${selection.masterTitle} : ${selection.treeId}`
      setCurriculaMap((prev) => {
        const next = new Map(prev)
        if (next.has(key)) {
          next.delete(key)
        } else {
          next.set(key, {
            productId: selection.productId,
            title: selection.masterTitle,
          })
        }
        return next
      })
    },
    [],
  )

  const removeCurriculum = useCallback((key: string) => {
    setCurriculaMap((prev) => {
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }, [])

  // --- School selection ---
  const onSchoolSelect = useCallback(
    (index: number) => {
      if (index === -1) {
        // Select all / deselect all
        if (selectedSchoolIndexes.length === districtSchools.length) {
          setSelectedSchoolIndexes([])
        } else {
          setSelectedSchoolIndexes(districtSchools.map((_s, i) => i))
        }
      } else {
        setSelectedSchoolIndexes((prev) =>
          prev.includes(index)
            ? prev.filter((i) => i !== index)
            : [...prev, index],
        )
      }
    },
    [districtSchools, selectedSchoolIndexes.length],
  )

  const schoolColumns: Column<DistrictSchoolItem>[] = useMemo(
    () => [
      {
        title: <strong>School Name</strong>,
        width: 250,
        rowContent(school: DistrictSchoolItem) {
          return school.name ?? `School ${school.id}`
        },
      },
      {
        title: <strong>ID</strong>,
        width: 60,
        rowContent(school: DistrictSchoolItem) {
          return school.id
        },
      },
      {
        title: <strong>Teachers</strong>,
        width: 80,
        rowContent(school: DistrictSchoolItem) {
          return school.teacher_count
        },
      },
      {
        title: <strong>Students</strong>,
        width: 80,
        rowContent(school: DistrictSchoolItem) {
          return school.student_count
        },
      },
    ],
    [],
  )

  // --- Submit ---
  const selectedSchools = useMemo(
    () => selectedSchoolIndexes.map((i) => districtSchools[i]),
    [selectedSchoolIndexes, districtSchools],
  )

  const productIds = useMemo(
    () => Array.from(curriculaMap.values()).map((v) => v.productId),
    [curriculaMap],
  )

  const handleAssign = useCallback(async () => {
    setConfirmOpen(false)
    setAssigning(true)
    const total = selectedSchools.length
    setProgress({ current: 0, total })

    let successCount = 0
    let errorCount = 0

    for (let i = 0; i < selectedSchools.length; i++) {
      const school = selectedSchools[i]
      try {
        const resp = await addCurricula(productIds, undefined, school.id)
        if (resp.errors && resp.errors.length > 0) {
          errorCount++
        } else {
          successCount++
        }
      } catch {
        errorCount++
      }
      setProgress((prev) => ({ ...prev, current: prev.current + 1 }))
    }

    setAssigning(false)

    if (errorCount === 0) {
      notify(
        `Successfully assigned ${curriculaMap.size} curricula to ${successCount} schools. This may take a while to process.`,
        { type: 'info' },
      )
    } else {
      notify(
        `Assigned to ${successCount} schools, ${errorCount} failed. Check individual schools for details.`,
        { type: 'error' },
      )
    }

    // Reset state
    setCurriculaMap(new Map())
    setSelectedSchoolIndexes([])
    setActiveStep(0)
    onComplete()
  }, [selectedSchools, productIds, curriculaMap.size, notify, onComplete])

  // --- Navigation ---
  const canNext =
    (activeStep === 0 && curriculaMap.size > 0) ||
    (activeStep === 1 && selectedSchoolIndexes.length > 0)

  const handleNext = () => {
    if (activeStep === 2) {
      setConfirmOpen(true)
    } else {
      setActiveStep((s) => s + 1)
    }
  }

  const handleBack = () => setActiveStep((s) => s - 1)

  if (assigning) {
    const pct =
      progress.total > 0
        ? Math.round((progress.current / progress.total) * 100)
        : 0
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Assigning curricula to schools...
        </Typography>
        <LinearProgress variant="determinate" value={pct} sx={{ mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {progress.current} of {progress.total} schools
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Select Curricula */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select the curricula to assign. Click a curriculum to
            select/deselect it.
          </Typography>
          <CurriculaSelection
            onSelect={toggleCurriculum}
            excluded={[]}
            activatePopUp={false}
          />
          {curriculaMap.size > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">
                Selected ({curriculaMap.size}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.from(curriculaMap.entries()).map(([key, val]) => (
                  <Chip
                    key={key}
                    label={val.title}
                    size="small"
                    onDelete={() => removeCurriculum(key)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Step 2: Select Schools */}
      {activeStep === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select the schools to assign the curricula to. Use the header
            checkbox to select all.
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Checkbox
              checked={
                selectedSchoolIndexes.length === districtSchools.length &&
                districtSchools.length > 0
              }
              indeterminate={
                selectedSchoolIndexes.length > 0 &&
                selectedSchoolIndexes.length < districtSchools.length
              }
              onChange={() => onSchoolSelect(-1)}
              size="small"
            />
            <Typography variant="body2" component="span">
              Select All ({selectedSchoolIndexes.length} of{' '}
              {districtSchools.length})
            </Typography>
          </Box>
          <Table
            columns={schoolColumns}
            items={districtSchools}
            onRowSelect={onSchoolSelect}
            selectedRows={selectedSchoolIndexes}
            overrideHeight={Math.min((districtSchools.length + 1) * 33, 400)}
          />
        </Box>
      )}

      {/* Step 3: Confirm */}
      {activeStep === 2 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Review the assignment below before confirming.
          </Alert>
          <Typography variant="subtitle2" gutterBottom>
            Curricula ({curriculaMap.size}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {Array.from(curriculaMap.values()).map((val, i) => (
              <Chip key={i} label={val.title} size="small" color="primary" />
            ))}
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Schools ({selectedSchools.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedSchools.map((s) => (
              <Chip
                key={s.id}
                label={s.name ?? `School ${s.id}`}
                size="small"
              />
            ))}
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will assign {curriculaMap.size} curricula to{' '}
            {selectedSchools.length} schools (
            {curriculaMap.size * selectedSchools.length} total operations). This
            may take a while.
          </Alert>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>
        <Button
          disabled={!canNext && activeStep !== 2}
          onClick={handleNext}
          variant="contained"
        >
          {activeStep === 2 ? 'Assign Curricula' : 'Next'}
        </Button>
      </Box>

      <RequestDialog
        open={confirmOpen}
        title={`Assign ${curriculaMap.size} curricula to ${selectedSchools.length} schools?`}
        content={[]}
        submitRequest={handleAssign}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  )
}
